import { DecimalPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AuthService } from '../../../../auth/auth.service';
import { DashboardEngineService } from '../../../services/dashboard-engine.service';

@Component({
  selector: 'app-performance-benchmark',
  standalone: true,
  imports: [DecimalPipe, NgClass, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './performance-benchmark.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerformanceBenchmarkComponent implements OnChanges {
  private engine = inject(DashboardEngineService);
  private auth = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  @Input() dashboardType!: string;
  @Input() filters: any = {};
  @Input() requiredPermission?: string;

  get hasPermission(): boolean {
    return !this.requiredPermission || this.auth.canAccess([this.requiredPermission]);
  }

  collectionData: any[] = [];
  srData: any[] = [];
  loading = false;
  error: string | null = null;
  activeTab: 'collection' | 'sr' = 'collection';

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.hasPermission) return;
    if (this.dashboardType) this.fetchData();
  }

  private fetchData(): void {
    this.loading = true;
    this.engine
      .getWidgetData(this.dashboardType, 'performanceBenchmarking', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const d = res.record?.data ?? res.record ?? {};
          this.collectionData = d.collection_performance ?? [];
          this.srData = d.sr_closure_performance ?? [];
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          this.error = err.status === 403 ? 'Access restricted' : err.status === 500 ? 'Service unavailable' : 'Unable to load data';
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  getPctBarWidth(pct: number): string {
    return `${Math.min(100, pct)}%`;
  }

  getPctColor(pct: number): string {
    if (pct >= 90) return 'bg-green-500';
    if (pct >= 70) return 'bg-yellow-400';
    return 'bg-red-400';
  }
}
